// Utilidades compartidas del módulo Activo Fijo (esquema activofijo).
// CRUD directo a PostgREST vía el atajo /rest/activofijo.<tabla> + helper RPC.
// Reutiliza los primitivos de UI de inventario/shared (Modal, Campo, Switch…).
import { apiClient } from '@/lib/api/client';
import { fechaLocal } from '@/lib/utils/fecha';
import i18n from '@/lib/i18n';

// Etiquetas traducibles: getters para que se resuelvan al renderizar (idioma vigente).
const tl = (k: string) => i18n.t(`activofijo:${k}`);

export { Campo, Switch, Modal, fieldCls, money } from '@/features/inventario/shared';

const base = (t: string) => `/rest/activofijo.${t}`;
const repr = { headers: { Prefer: 'return=representation' } };

// CRUD con borrado LÓGICO (deleted_at).
export const af = {
  list: async <T,>(t: string, query = ''): Promise<T[]> => (await apiClient.get<T[]>(`${base(t)}${query}`)).data,
  create: async (t: string, body: unknown) => (await apiClient.post(base(t), body, repr)).data,
  update: async (t: string, id: string, body: unknown) => (await apiClient.patch(`${base(t)}?id=eq.${id}`, body, repr)).data,
  remove: async (t: string, id: string) =>
    (await apiClient.patch(`${base(t)}?id=eq.${id}`, { deleted_at: new Date().toISOString() }, repr)).data,
};

// RPC del esquema (Content-Profile: activofijo) — para fases siguientes.
const AF = { headers: { 'Content-Profile': 'activofijo', 'Accept-Profile': 'activofijo' } };
export async function afRpc<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  return (await apiClient.post<T>(`/rpc/${fn}`, args, AF)).data;
}

// Combos de dimensiones/terceros (otros esquemas).
const CONT = { headers: { 'Content-Profile': 'contabilidad', 'Accept-Profile': 'contabilidad' } };
const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
export interface Opcion { id: string; codigo?: string | null; nombre?: string | null; razon_social?: string | null }
export const listarCentros = async (): Promise<Opcion[]> => (await apiClient.post<Opcion[]>('/rpc/cont_centros', {}, CONT)).data || [];
export const listarProyectos = async (): Promise<Opcion[]> => (await apiClient.post<Opcion[]>('/rpc/cont_proyectos', {}, CONT)).data || [];
export const listarProveedores = async (): Promise<Opcion[]> => (await apiClient.post<Opcion[]>('/rpc/sri_partners', {}, FIN)).data || [];

export const ESTADOS_ACTIVO = [
  { v: 'activo', get l() { return tl('estados.activo'); } }, { v: 'borrador', get l() { return tl('estados.borrador'); } },
  { v: 'dado_de_baja', get l() { return tl('estados.dado_de_baja'); } }, { v: 'vendido', get l() { return tl('estados.vendido'); } },
];
export const ESTADO_ACTIVO_CLS: Record<string, string> = {
  activo: 'bg-estable-soft text-estable', borrador: 'bg-muted text-muted-foreground',
  dado_de_baja: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  vendido: 'bg-pendiente-soft text-pendiente',
};

export interface ActivoRow {
  id: string; placa: string; nombre: string; estado: string; origen: string;
  costo: number; valor_residual: number; deprec_acumulada: number; deterioro_acumulado: number; valor_libros: number;
  fecha_adquisicion: string | null; fecha_inicio_depreciacion: string | null; vida_util_meses: number | null;
  categoria: string; depreciable: boolean; ubicacion: string | null; custodio: string | null;
  centro_costo: string | null; proyecto: string | null;
}
export interface MovRow { id: string; fecha: string; placa: string; activo: string; ubic_anterior: string | null; ubic_nueva: string | null; cust_anterior: string | null; cust_nuevo: string | null; cc_anterior: string | null; cc_nuevo: string | null; observaciones: string | null; }
// `salida` = fecha de baja o venta. La depreciación fiscal se detiene ahí: un
// activo que ya no es de la empresa no genera gasto deducible.
export interface FiscalRow { placa: string; nombre: string; categoria: string; tarifa: number; costo: number; contable_anio: number; fiscal_anio: number; diferencia: number; contable_acum: number; fiscal_acum: number; salida?: string | null; }
export interface Componente { id: string; nombre: string; costo: number; valor_residual: number; vida_util_meses: number | null; metodo: string; fecha_inicio_depreciacion: string | null; es_activo: boolean; }
export interface MejoraRow { id: string; fecha: string; importe: number; extender_meses: number; costo_anterior: number; costo_nuevo: number; estado: string; placa: string; activo: string; asiento_numero: number | null; }
// `importe` puede ser NEGATIVO (desvalorización). El reparto entre patrimonio y
// resultados es lo que manda la NIC 16 §39-40 y depende de la historia del
// propio activo: sin verlo, «revalorización de 500» no dice nada.
export interface RevalRow { id: string; fecha: string; importe: number; costo_anterior: number; costo_nuevo: number; estado: string; placa: string; activo: string; asiento_numero: number | null;
  a_patrimonio?: number; a_resultados?: number; acum_anterior?: number | null; acum_nueva?: number | null;
  tecnica?: string | null; imp_diferido?: number; }
export interface ImpDiferido {
  anio: number; tarifa: number; base_diferencia: number;
  saldo_requerido: number; saldo_anterior: number; movimiento: number;
}
export interface DeterRow { id: string; fecha: string; tipo: string; importe: number; deterioro_nuevo: number; estado: string; placa: string; activo: string; asiento_numero: number | null; }

export const METODOS = [
  { v: 'linea_recta', get l() { return tl('metodos.linea_recta'); } },
  { v: 'unidades_produccion', get l() { return tl('metodos.unidades_produccion'); } },
  { v: 'saldos_decrecientes', get l() { return tl('metodos.saldos_decrecientes'); } },
];
export const CONVENCION = [
  { v: 'mes_completo', get l() { return tl('convencion.mes_completo'); } },
  { v: 'prorrateo_dias', get l() { return tl('convencion.prorrateo_dias'); } },
];
export const metodoLabel = (v: string) => METODOS.find((m) => m.v === v)?.l ?? v;

export interface Categoria {
  id: string; codigo: string | null; nombre: string; depreciable: boolean; metodo: string;
  vida_util_meses: number | null; valor_residual_pct: number; tarifa_fiscal_pct: number | null; es_activo: boolean;
}
export interface Ubicacion {
  id: string; codigo: string | null; nombre: string; parent_id: string | null; es_activo: boolean;
}
export interface Custodio {
  id: string; codigo: string | null; nombre: string; identificacion: string | null;
  cargo: string | null; email: string | null; es_activo: boolean;
}
// ---- 792/793/794 · revisión de vida útil, ficha completa y NIIF 16 ----
export interface RevisionRow {
  id: string; anio: number; fecha: string; estado: string; responsable: string | null;
  observaciones: string | null; n_revisados: number; n_ajustados: number;
  cerrada_at: string | null; cerrada_por: string | null; pendientes: number;
}
export interface RevisionLinea {
  id: string; placa: string; activo: string; categoria: string | null; motivo: string;
  vida_anterior: number | null; residual_anterior: number | null; libros_anterior: number;
  decision: string; vida_nueva: number | null; residual_nuevo: number | null;
  justificacion: string | null; revisado_por: string | null; revisado_at: string | null;
  fecha_inicio_depreciacion: string | null;
}
export const MOTIVO_REVISION: Record<string, string> = {
  get totalmente_depreciado() { return tl('motivo_revision.totalmente_depreciado'); },
  get vida_vencida() { return tl('motivo_revision.vida_vencida'); },
  get indicio_deterioro() { return tl('motivo_revision.indicio_deterioro'); },
  get valor_significativo() { return tl('motivo_revision.valor_significativo'); },
};

export interface Mantenimiento {
  id: string; fecha: string; tipo: string; descripcion: string;
  proveedor: string | null; proveedor_id: string | null; costo: number;
  horas_parada: number | null; proxima_fecha: string | null; mejora_id: string | null;
  observaciones: string | null;
}
export interface Poliza {
  id: string; clase: string; numero: string | null; emisor: string | null;
  desde: string; hasta: string; suma_asegurada: number | null; prima: number | null;
  deducible: number | null; cobertura: string | null; vigente: boolean; dias_para_vencer: number;
}
export interface FichaAF {
  mantenimientos: Mantenimiento[];
  polizas: Poliza[];
  resumen: {
    n_mantenimientos: number; costo_mantenimiento: number;
    costo_vs_activo_pct: number | null; tiene_foto: boolean; cobertura_vigente: boolean;
  };
}

export interface ArriendoRow {
  id: string; codigo: string | null; descripcion: string; arrendador: string | null;
  fecha_inicio: string; plazo_meses: number; periodicidad: string; cuota: number;
  modalidad: string; tasa_anual: number; exento: boolean; motivo_exencion: string | null;
  pasivo_inicial: number | null; activo_inicial: number | null; estado: string;
  placa_activo: string | null; activo_id: string | null;
  n_cuotas: number; devengadas: number; saldo_pasivo: number | null;
}
export interface ArriendoCuota {
  id: string; numero: number; fecha: string; saldo_inicial: number; cuota: number;
  interes: number; amortizacion: number; saldo_final: number;
  devengada_at: string | null; asiento_numero: number | null;
}

export const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
// Nombres de mes en el idioma vigente (MESES queda en español para las pantallas aún no traducidas).
export const mesesTr = (): string[] => MESES.map((_, i) => tl(`meses.m${i + 1}`));
// Una fila por UNIDAD DEPRECIABLE, no por activo: el «resto» del activo
// (componente_id nulo) y cada componente con vida útil propia (NIC 16 §43).
// Por eso la clave de React es activo+componente, no sólo el activo.
export interface DeprecItem { activo_id: string; componente_id: string | null; placa: string; nombre: string; categoria: string; metodo: string; base: number; acumulada_previa: number; importe: number; acumulada_resultante: number; }
// El sello del período (788): quién lo cerró y cuándo, quién lo reabrió y por
// qué. `sellada` dice si se guardó la huella del contenido al cerrar; el acta
// la recalcula y delata si las cifras cambiaron después.
export interface CorridaRow { id: string; anio: number; mes: number; fecha: string; estado: string; total_depreciacion: number; n_activos: number; asiento_id: string | null; asiento_numero: number | null;
  cerrada_por?: string | null; cerrada_at?: string | null;
  reabierta_por?: string | null; reabierta_at?: string | null; motivo_reapertura?: string | null; sellada?: boolean; }
export interface CorridaActa {
  anio: number; mes: number; estado: string; n_activos: number; total_depreciacion: number;
  cerrada_por: string | null; cerrada_at: string | null;
  reabierta_por: string | null; reabierta_at: string | null; motivo_reapertura: string | null;
  hash_cierre: string | null; hash_actual: string; intacta: boolean;
  unidades: { placa: string; unidad: string; importe: number; acumulada: number }[];
}
// `requerido` marca las cuatro cuentas sin las cuales el módulo no arranca: la
// carga inicial y la corrida de depreciación fallan, y el fallo llega envuelto
// en un aviso que es fácil pasar por alto. La pantalla las señala ANTES.
export interface CuentaMap { concepto: string; label: string; cuenta_id: string | null; codigo: string | null; nombre: string | null; requerido?: boolean; ayuda?: string | null; }
export const ESTADO_CORRIDA_CLS: Record<string, string> = {
  borrador: 'bg-pendiente-soft text-pendiente', contabilizada: 'bg-estable-soft text-estable',
  cerrada: 'bg-muted text-muted-foreground',
};
export const MOTIVOS_BAJA = [
  { v: 'obsolescencia', get l() { return tl('motivos_baja.obsolescencia'); } }, { v: 'robo', get l() { return tl('motivos_baja.robo'); } },
  { v: 'siniestro', get l() { return tl('motivos_baja.siniestro'); } }, { v: 'donacion', get l() { return tl('motivos_baja.donacion'); } }, { v: 'otro', get l() { return tl('motivos_baja.otro'); } },
];
export interface BajaRow { id: string; fecha: string; motivo: string; costo: number; deprec_acumulada: number; valor_en_libros: number; estado: string; observaciones: string | null; placa: string; activo: string; asiento_numero: number | null; }
export interface VentaRow { id: string; fecha: string; precio_venta: number; costo: number; deprec_acumulada: number; valor_en_libros: number; utilidad: number; estado: string; factura_ref: string | null; placa: string; activo: string; comprador: string | null; asiento_numero: number | null; }
export interface RegRow { placa: string; nombre: string; categoria: string; ubicacion: string | null; custodio: string | null; centro_costo: string | null; fecha_adquisicion: string | null; estado: string; depreciable: boolean; costo: number; valor_residual: number; deprec_acumulada: number; valor_libros: number; }
export interface Rollforward { costo_inicial: number; altas: number; retiros_costo: number; costo_final: number; deprec_inicial: number; deprec_periodo: number; deprec_retirada: number; deprec_final: number; valor_libros_final: number; }
export interface DistribRow { grupo: string; n_activos: number; costo_total: number; deprec_total: number; valor_libros_total: number; }
export interface ProyRow { mes: string; importe: number; }
export interface DepRow { placa: string; nombre: string; categoria: string; centro_costo: string | null; costo: number; valor_residual: number; fecha_inicio_depreciacion: string | null; deprec_acumulada: number; }
export interface Concil { costo_registro: number; costo_mayor: number; costo_dif: number; deprec_registro: number; deprec_mayor: number; deprec_dif: number; cuenta_costo_mapeada: boolean; cuenta_deprec_mapeada: boolean; }

// Exporta filas a Excel (carga xlsx dinámicamente).
export async function exportarXls(filas: Record<string, unknown>[], hoja: string, archivo: string) {
  if (!filas.length) return;
  const XLSX = await import('xlsx');
  const ws = XLSX.utils.json_to_sheet(filas);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, hoja);
  XLSX.writeFile(wb, `${archivo}_${fechaLocal()}.xlsx`);
}

export interface TomaRow { id: string; fecha: string; estado: string; responsable: string | null; ubicacion: string | null; esperados: number; encontrados: number; faltantes: number; sobrantes: number; }
export interface TomaDetalle { id: string; placa: string; activo: string; estado: string; es_sobrante: boolean; ubic_esperada: string | null; ubic_hallada: string | null; cust_esperado: string | null; cust_hallado: string | null; observacion: string | null; ubic_incorrecta: boolean; }
export const ESTADO_DET_CLS: Record<string, string> = {
  pendiente: 'bg-muted text-muted-foreground', encontrado: 'bg-estable-soft text-estable',
  no_encontrado: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};
export interface ParametrosAF {
  id: string; metodo_default: string; convencion_parcial: string;
  umbral_capitalizacion: number; prefijo_placa: string; siguiente_placa: number; placa_relleno: number;
  // 789 · NIC 16 §35 y NIC 12
  reval_tecnica: string; tarifa_impuesto_renta_pct: number;
}
export const REVAL_TECNICA = [
  { v: 'proporcional', get l() { return tl('reval.proporcional'); } },
  { v: 'eliminacion', get l() { return tl('reval.eliminacion'); } },
];
